import React, { useState, useEffect, useRef, useMemo } from "react";
import { Search, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function matchesQuery(label, query) {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean).slice(0, 3);
  if (words.length === 0) return true;
  const target = label.toLowerCase();
  return words.every((w) => target.includes(w));
}

export default function SearchableSelect({ label, options, value, onChange, placeholder, testId, disabled, allowManualEntry }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!value && document.activeElement === inputRef.current) return;
    const selected = options.find((o) => o.value === value);
    setQuery(selected ? selected.label : (value || ""));
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    if (!open) return [];
    const q = query.trim().toLowerCase();
    const results = q ? options.filter((o) => matchesQuery(o.label, query)) : options;
    const ranked = q
      ? [...results].sort((a, b) => {
          const rank = (l) => (l === q ? 0 : l.startsWith(q) ? 1 : 2);
          const al = a.label.toLowerCase(), bl = b.label.toLowerCase();
          return rank(al) - rank(bl) || al.localeCompare(bl);
        })
      : results;
    return ranked.slice(0, 50);
  }, [options, query, open]);

  return (
    <div className="relative" ref={wrapperRef}>
      {label && <Label className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">{label}</Label>}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--brand-secondary)]" />
        <Input
          ref={inputRef}
          data-testid={testId}
          disabled={disabled}
          className="pl-9 pr-9 focus-visible:ring-[var(--brand-primary)]"
          placeholder={disabled ? "Select a country first" : (placeholder || "Search...")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            onChange("");
          }}
          onFocus={() => setOpen(true)}
        />
        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--brand-secondary)] pointer-events-none" />
      </div>
      {open && (filtered.length > 0 || (allowManualEntry && query.trim())) && (
        <div data-testid={`${testId}-dropdown`} className="absolute z-30 mt-1 w-full bg-white border border-[#E5E1DA] rounded-md shadow-lg max-h-56 overflow-y-auto">
          {filtered.map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-testid={`${testId}-option-${opt.value}`}
              onClick={() => {
                setQuery(opt.label);
                onChange(opt.value);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_90%)] flex items-center gap-2 text-sm border-b border-[#E5E1DA] last:border-0"
            >
              {opt.flag && <span>{opt.flag}</span>}
              <span>{opt.label}</span>
            </button>
          ))}
          {allowManualEntry && query.trim() && !options.some((o) => o.label.toLowerCase() === query.trim().toLowerCase()) && (
            <button
              type="button"
              data-testid={`${testId}-manual-entry`}
              onClick={() => {
                onChange(query.trim());
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_90%)] flex items-center gap-2 text-sm text-[#54595f] italic"
            >
              <Search size={14} className="shrink-0" />
              <span>Can't find it? Use "<strong className="not-italic text-[var(--brand-primary)]">{query.trim()}</strong>" as typed</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
